import * as Yup from 'yup';
import { errorMessages } from '~/constants/errorMessages.ts';
import {
  optionAccountHolderStatus,
  showAccountHolderStatusGiin,
  showBeneficiaryOwners,
} from '~/modules/identification/forms/TaxResidencyAdditionalInformation/constants.ts';
import { regularExpressions } from '~/constants/regularExpressions.ts';
import { IBAN_MAX_LENGTH } from '~/constants/common.ts';
import { TOwner } from '~/modules/identification/forms/OwnershipStructure/types.ts';

export const accountHolderStatusValidationSchema = Yup.object().shape(
  {
    selectedOption: Yup.string().required(errorMessages.RADIO_OPTION_REQUIRED),
    giin: Yup.string()
      .nullable()
      .when('selectedOption', function ([selectedOption], schema) {
        return showAccountHolderStatusGiin.includes(selectedOption)
          ? schema.matches(regularExpressions.REGISTRY_RECORD_GIIN, {
              message: errorMessages.GIIN_ENTRY_FORMAT,
              excludeEmptyString: true,
            })
          : schema.notRequired();
      }),
    securitiesMarketName: Yup.string()
      .nullable()
      .when(['selectedOption', 'publicCompanyName'], {
        is: (selectedOption: string, publicCompanyName: string) =>
          selectedOption === optionAccountHolderStatus.option4 && !publicCompanyName,
        then: (schema) =>
          schema
            .required('Має бути заповнене одне із полів')
            .max(500, errorMessages.MAXIMUM_NUMBER_OF_CHARACTERS(500)),
        otherwise: (schema) =>
          schema.notRequired().max(500, errorMessages.MAXIMUM_NUMBER_OF_CHARACTERS(500)),
      }),
    publicCompanyName: Yup.string()
      .nullable()
      .when(['selectedOption', 'securitiesMarketName'], {
        is: (selectedOption: string, securitiesMarketName: string) =>
          selectedOption === optionAccountHolderStatus.option4 && !securitiesMarketName,
        then: (schema) =>
          schema
            .required('Має бути заповнене одне із полів')
            .max(500, errorMessages.MAXIMUM_NUMBER_OF_CHARACTERS(500)),
        otherwise: (schema) =>
          schema.notRequired().max(500, errorMessages.MAXIMUM_NUMBER_OF_CHARACTERS(500)),
      }),
  },
  [['securitiesMarketName', 'publicCompanyName']]
);

export const taxResidenciesValidationSchema = Yup.array().of(
  Yup.object().shape({
    jurisdictionCountry: Yup.object()
      .shape({
        value: Yup.string().nullable(),
        label: Yup.string().required(errorMessages.FIELD_REQUIRED),
      })
      .nullable()
      .required(errorMessages.FIELD_REQUIRED),
    tin: Yup.string()
      .nullable()
      .test('tin-required', errorMessages.FIELD_REQUIRED, function (value) {
        const { tinUnavailableReason } = this.parent;
        if (!value && !tinUnavailableReason) {
          return this.createError({ path: this.path, message: errorMessages.FIELD_REQUIRED });
        }
        return true;
      }),
    tinUnavailableReason: Yup.string()
      .nullable()
      .test('tinUnavailableReason-required', errorMessages.FIELD_REQUIRED, function (value) {
        const { tin } = this.parent;
        if (!value && !tin) {
          return this.createError({ path: this.path, message: errorMessages.FIELD_REQUIRED });
        }
        return true;
      }),
    tinUnavailableExplanation: Yup.string()
      .nullable()
      .when('tinUnavailableReason', function ([tinUnavailableReason], schema) {
        return ['B', 'C'].includes(tinUnavailableReason)
          ? schema.required(errorMessages.FIELD_REQUIRED)
          : schema.notRequired();
      }),
  })
);

export const validationSchemaAccounts = Yup.array()
  .of(
    Yup.object().shape({
      iban: Yup.string()
        .nullable()
        .test('iban-length', errorMessages.INCOMPLETE_IBAN, function (value) {
          if (!value) return true;
          return value.length === IBAN_MAX_LENGTH;
        })
        .test('iban-bank-mfo', errorMessages.INVALID_IBAN, function (value) {
          if (!value || value.length !== IBAN_MAX_LENGTH) return true;
          const { bankMFO } = this.parent;
          return Boolean(bankMFO);
        }),
      openDate: Yup.string()
        .nullable()
        .when('iban', function ([iban], schema) {
          return iban ? Yup.string().required(errorMessages.FIELD_REQUIRED) : schema.notRequired();
        }),
      bankMFO: Yup.mixed().nullable(),
    })
  )
  .test('unique-bank-accounts', errorMessages.DUPLICATE_ACCOUNT_NUMBER, function (accounts) {
    if (!accounts) return true;
    const errors: Yup.ValidationError[] = [];
    accounts.forEach((account, index) => {
      if (!account.iban || !account.openDate) return;
      const isDuplicate = accounts.some(
        (other, otherIndex) =>
          otherIndex !== index && other.iban === account.iban && other.openDate === account.openDate
      );
      if (isDuplicate) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].iban`,
            message: errorMessages.DUPLICATE_ACCOUNT_NUMBER,
          })
        );
      }
    });
    if (errors.length > 0) throw new Yup.ValidationError(errors);
    return true;
  });

export const validationSchemaOrganizations = Yup.array().of(
  Yup.object().shape({
    name: Yup.string()
      .required(errorMessages.FIELD_REQUIRED)
      .max(150, errorMessages.MAXIMUM_NUMBER_OF_CHARACTERS(150))
      .matches(
        regularExpressions.SPACE_CYRILLIC_LATIN_NUMBERS,
        errorMessages.LATIN_AND_UKR_CHARACTERS_ONLY
      ),
    controllingPersonStatus: Yup.object()
      .shape({
        value: Yup.string().nullable(),
        label: Yup.string().required(errorMessages.FIELD_REQUIRED),
      })
      .required(errorMessages.FIELD_REQUIRED),
    accounts: validationSchemaAccounts,
  })
);

export const validationSchemaControllingPersons = Yup.array().of(
  Yup.object().shape({
    isTaxResidentOtherJurisdiction: Yup.boolean().required(errorMessages.YES_NO_REQUIRED),
    salutation: Yup.object()
      .nullable()
      .when('isTaxResidentOtherJurisdiction', {
        is: true,
        then: (schema) =>
          schema
            .shape({
              value: Yup.string().nullable(),
              label: Yup.string().required(errorMessages.FIELD_REQUIRED),
            })
            .required(errorMessages.FIELD_REQUIRED),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    taxResidencies: Yup.array()
      .nullable()
      .when('isTaxResidentOtherJurisdiction', {
        is: true,
        then: () => taxResidenciesValidationSchema,
        otherwise: (schema) => schema.notRequired().strip(),
      }),
    organizations: Yup.array()
      .nullable()
      .when('isTaxResidentOtherJurisdiction', {
        is: true,
        then: () => validationSchemaOrganizations,
        otherwise: (schema) => schema.notRequired().strip(),
      }),
  })
);

export const validationSchema = (beneficiaryOwners: TOwner[]) =>
  Yup.object().shape({
    accountHolderStatus: accountHolderStatusValidationSchema,
    taxResidencies: taxResidenciesValidationSchema,
    controllingPersons: Yup.array().when(['accountHolderStatus.selectedOption'], {
      is: (selectedOption: string) =>
        beneficiaryOwners?.length > 0 && showBeneficiaryOwners.includes(selectedOption),
      then: () => validationSchemaControllingPersons,
      otherwise: (schema) => schema.nullable().notRequired().strip(),
    }),
  });
